/**
 * richText.js — Pure utility functions for segment-based rich text.
 */

/** Default styles for a new segment */
export const DEFAULT_SEGMENT_STYLES = {
  fontFamily: 'Helvetica',
  fontSize: 14,
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  color: '#000000',
};

/** Segment-level style keys (per-character formatting) */
const SEGMENT_STYLE_KEYS = ['fontFamily', 'fontSize', 'bold', 'italic', 'underline', 'strikethrough', 'color'];

/** Box-level style keys (apply to the whole text box container) */
export const BOX_STYLE_KEYS = ['alignment', 'backgroundColor', 'lineHeight'];

/**
 * Convert a legacy edit (content string + styles object) into a segments array.
 * If the edit already has segments, returns them as-is.
 */
export function editToSegments(edit) {
  if (edit.segments && edit.segments.length > 0) return edit.segments;
  const styles = {};
  for (const key of SEGMENT_STYLE_KEYS) {
    styles[key] = edit.styles?.[key] ?? DEFAULT_SEGMENT_STYLES[key];
  }
  return [{ text: edit.content || '', styles }];
}

/**
 * Concatenate all segment text into a single plain string.
 */
export function segmentsToText(segments) {
  return segments.map((s) => s.text).join('');
}

/**
 * Check if two style objects are equal (segment-level keys only).
 */
export function stylesEqual(a, b) {
  for (const key of SEGMENT_STYLE_KEYS) {
    if ((a[key] ?? DEFAULT_SEGMENT_STYLES[key]) !== (b[key] ?? DEFAULT_SEGMENT_STYLES[key])) return false;
  }
  return true;
}

/**
 * Merge adjacent segments that have identical styles.
 * Returns a new array (never mutates input).
 */
export function mergeAdjacentSegments(segments) {
  if (segments.length === 0) return [];
  const result = [{ text: segments[0].text, styles: { ...segments[0].styles } }];
  for (let i = 1; i < segments.length; i++) {
    const prev = result[result.length - 1];
    if (stylesEqual(prev.styles, segments[i].styles)) {
      prev.text += segments[i].text;
    } else {
      result.push({ text: segments[i].text, styles: { ...segments[i].styles } });
    }
  }
  // Remove empty segments (except if that's all we have)
  const filtered = result.filter((s) => s.text.length > 0);
  return filtered.length > 0 ? filtered : [{ text: '', styles: { ...result[0].styles } }];
}

/**
 * Get the styles at a specific character offset.
 * Used by the toolbar to show what formatting the cursor is sitting in.
 */
export function getStylesAtOffset(segments, offset) {
  let pos = 0;
  for (const seg of segments) {
    if (offset <= pos + seg.text.length) {
      return { ...seg.styles };
    }
    pos += seg.text.length;
  }
  return segments.length > 0 ? { ...segments[segments.length - 1].styles } : { ...DEFAULT_SEGMENT_STYLES };
}

/**
 * Apply a style change to a character range within segments.
 * Splits segments at the range boundaries, applies the style to the selected range,
 * then merges adjacent segments with identical styles.
 */
export function applyStyleToRange(segments, start, end, key, value) {
  if (start >= end) return segments;

  const result = [];
  let pos = 0;

  for (const seg of segments) {
    const segStart = pos;
    const segEnd = pos + seg.text.length;

    if (segEnd <= start || segStart >= end) {
      // Entirely outside the range
      result.push({ text: seg.text, styles: { ...seg.styles } });
    } else {
      // This segment overlaps the range — split it
      if (segStart < start) {
        result.push({ text: seg.text.slice(0, start - segStart), styles: { ...seg.styles } });
      }
      const overlapStart = Math.max(0, start - segStart);
      const overlapEnd = Math.min(seg.text.length, end - segStart);
      result.push({
        text: seg.text.slice(overlapStart, overlapEnd),
        styles: { ...seg.styles, [key]: value },
      });
      if (segEnd > end) {
        result.push({ text: seg.text.slice(end - segStart), styles: { ...seg.styles } });
      }
    }

    pos = segEnd;
  }

  return mergeAdjacentSegments(result);
}

/**
 * Apply a style change to ALL segments (used when no text is selected).
 */
export function applyStyleToAll(segments, key, value) {
  return mergeAdjacentSegments(
    segments.map((seg) => ({ text: seg.text, styles: { ...seg.styles, [key]: value } }))
  );
}

/**
 * Check whether a style key is segment-level or box-level.
 */
export function isBoxLevelStyle(key) {
  return BOX_STYLE_KEYS.includes(key);
}

/**
 * Compute the glyph-tight render bounds of a text edit.
 *
 * The edit's container (edit.x, edit.y, edit.width, edit.height) is usually
 * wider and taller than the actual rendered glyphs. Anywhere we need to
 * "erase" the visual extent of a baked text overlay — the on-screen MaskLayer
 * preview, the PDF save's bakedBounds whiteout, or the deletion whiteout —
 * using the container box paints a slab across the form lines, table grids,
 * etc. that sit beneath the text but outside the glyph area.
 *
 * This helper returns the tight rectangle around the rendered glyphs given a
 * `measureWidth(text, segmentStyles)` callback. The caller picks the right
 * measurement strategy for their environment:
 *   - Main thread:  use a canvas 2D context's measureText (in MaskLayer).
 *   - Save worker:  use pdf-lib's font.widthOfTextAtSize (in pdfOperations).
 *
 * Returned bounds use the SAME coordinate origin as edit.x/edit.y (page
 * top-left, y growing downward). Multi-line takes the widest line; height
 * grows with the line count. Empty content returns null.
 */
export function computeTextGlyphBounds(edit, measureWidth) {
  if (!edit) return null;
  const segments = editToSegments(edit);
  const fullText = segmentsToText(segments);
  if (!fullText) return null;

  const lineHeightMultiplier = edit.styles?.lineHeight || 1.3;

  // Flatten segments into a per-char style array (same shape renderTextEdit
  // uses) so we can group consecutive same-style runs per line.
  const charStyles = [];
  for (const seg of segments) {
    const segStyles = seg.styles || {};
    for (let i = 0; i < seg.text.length; i++) charStyles.push(segStyles);
  }

  const sameRunStyles = (a, b) => (
    a && b
    && a.fontFamily === b.fontFamily
    && a.fontSize === b.fontSize
    && a.bold === b.bold
    && a.italic === b.italic
  );

  const lines = fullText.split('\n');
  let maxLineWidth = 0;
  let charOffset = 0;
  for (const line of lines) {
    if (!line) { charOffset += 1; continue; }
    let lineWidth = 0;
    let runStart = 0;
    for (let ci = 1; ci <= line.length; ci++) {
      const prevStyle = charStyles[charOffset + runStart];
      const currStyle = ci < line.length ? charStyles[charOffset + ci] : null;
      if (ci === line.length || !sameRunStyles(prevStyle, currStyle)) {
        const runText = line.slice(runStart, ci);
        if (runText) {
          try { lineWidth += measureWidth(runText, prevStyle || {}); }
          catch (_) { lineWidth += runText.length * (prevStyle?.fontSize || 14) * 0.5; }
        }
        runStart = ci;
      }
    }
    if (lineWidth > maxLineWidth) maxLineWidth = lineWidth;
    charOffset += line.length + 1;
  }

  // Use the largest font size across segments to size the line height.
  let maxFontSize = 14;
  for (const seg of segments) {
    const s = seg.styles?.fontSize;
    if (typeof s === 'number' && s > maxFontSize) maxFontSize = s;
  }

  // Match renderTextEdit's geometry: with padding=0, the first line's
  // baseline lands at edit.y + fontSize. Letter caps extend ~0.7 * fontSize
  // ABOVE the baseline, descenders ~0.2 * fontSize BELOW. So the rendered
  // glyph extent in top-down coords is:
  //   top    = edit.y + fontSize - 0.7 * fontSize = edit.y + 0.3 * fontSize
  //   bottom = edit.y + fontSize + 0.2 * fontSize = edit.y + 1.2 * fontSize
  //   height = 0.9 * fontSize per single line
  // For N lines, each additional line adds fontSize * lineHeightMultiplier.
  const visibleLines = Math.max(1, lines.filter((l) => l !== '').length);
  const capOffset = maxFontSize * 0.3;          // gap above the cap
  const glyphTallness = maxFontSize * 0.9;       // cap → descender (one line)
  const totalHeight = glyphTallness
    + (visibleLines - 1) * maxFontSize * lineHeightMultiplier;

  return {
    x: edit.x,
    y: edit.y + capOffset,
    width: Math.ceil(maxLineWidth),
    height: Math.ceil(totalHeight),
  };
}
